/**
 * PWA runtime: the service worker registration, the update prompt state, the
 * captured Android install prompt, and the sign-out cache purge.
 *
 * `startPwa()` runs once from main.tsx, before React, so the browser's
 * `beforeinstallprompt` (which can fire before anyone signs in) is never
 * missed. Components read the state with `usePwaState()`.
 */
import { useSyncExternalStore } from 'react';
import { desktopBridge } from '../desktop/bridge.js';

/** Chromium's install prompt event (not in lib.dom). */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

export interface PwaState {
  /** A captured Android/Chromium install prompt, usable once. */
  deferredPrompt: BeforeInstallPromptEvent | null;
  /** The app was installed during this visit (appinstalled). */
  installedNow: boolean;
  /** A new build's worker is installed and waiting for "Reload". */
  updateWaiting: boolean;
}

let state: PwaState = { deferredPrompt: null, installedNow: false, updateWaiting: false };
const listeners = new Set<() => void>();
let registration: ServiceWorkerRegistration | null = null;
let started = false;

function set(patch: Partial<PwaState>): void {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function getPwaState(): PwaState {
  return state;
}

export function usePwaState(): PwaState {
  return useSyncExternalStore(subscribe, getPwaState, getPwaState);
}

/** Use the captured prompt (once). Resolves to the person's choice. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const ev = state.deferredPrompt;
  if (!ev) return 'unavailable';
  set({ deferredPrompt: null });
  try {
    await ev.prompt();
    const choice = await ev.userChoice;
    return choice.outcome;
  } catch {
    return 'unavailable';
  }
}

/**
 * UI analytics for the install sheet. Velora has no client UI-event
 * endpoint yet, so this is a DOM event (`velora:ui-event`) a future
 * collector can listen for; it carries the outcome and platform only,
 * never anything about the user or patients.
 */
export function trackUiEvent(name: string, detail: Record<string, string>): void {
  try {
    window.dispatchEvent(new CustomEvent('velora:ui-event', { detail: { name, ...detail } }));
  } catch {
    // Analytics never breaks the UI.
  }
}

/** The page's own entry script (/assets/index-<hash>.js), or null in dev. */
export function pageEntryPath(doc: Document = document): string | null {
  const el = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  if (!el) return null;
  try {
    return new URL(el.getAttribute('src') ?? '', window.location.href).pathname;
  } catch {
    return null;
  }
}

/**
 * Asks a (waiting) worker which build it serves. Resolves to its shell URL
 * list, or null when it does not answer (an older worker without GET_BUILD).
 */
function askWorkerShell(worker: ServiceWorker, timeoutMs = 3000): Promise<string[] | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: string[] | null) => {
      if (done) return;
      done = true;
      resolve(v);
    };
    try {
      const ch = new MessageChannel();
      ch.port1.onmessage = (e: MessageEvent) => {
        const d = e.data as { type?: string; shell?: unknown } | null;
        finish(d?.type === 'BUILD' && Array.isArray(d.shell) ? (d.shell as string[]) : null);
      };
      worker.postMessage({ type: 'GET_BUILD' }, [ch.port2]);
      setTimeout(() => finish(null), timeoutMs);
    } catch {
      finish(null);
    }
  });
}

/**
 * Whether a waiting worker is really newer than the running page.
 *
 * Navigations are network only, so after a deploy a reload (or a new tab, or a
 * pop-out) already runs the NEW build while the OLD worker stays in control
 * until every tab it controls is gone. The browser then reports a waiting
 * worker on every load, and the old code showed "New version available" for
 * a version the page was already running (Zac, 2026-10-28). The page is
 * current when its own entry script is in the waiting worker's shell list.
 */
export function isUpdateForPage(shell: readonly string[] | null, entry: string | null): boolean {
  if (!shell || !entry) return true; // cannot tell: keep the old behaviour
  return !shell.includes(entry);
}

function watchForUpdate(reg: ServiceWorkerRegistration): void {
  let checking = false;
  const check = () => {
    // A waiting worker with a controller in place is an update, not a first install.
    const waiting = reg.waiting;
    if (!waiting || !navigator.serviceWorker.controller || checking) return;
    checking = true;
    void askWorkerShell(waiting)
      .then((shell) => {
        if (isUpdateForPage(shell, pageEntryPath())) {
          set({ updateWaiting: true });
        } else {
          // This page already runs the waiting build: let it take over quietly.
          // No reload; other open tabs that are older see the prompt themselves.
          waiting.postMessage({ type: 'SKIP_WAITING' });
          set({ updateWaiting: false });
        }
      })
      .finally(() => {
        checking = false;
      });
  };
  check();
  reg.addEventListener('updatefound', () => {
    const next = reg.installing;
    next?.addEventListener('statechange', () => {
      if (next.state === 'installed') check();
    });
  });
}

/** "Reload" on the update prompt: the waiting worker takes over, then the page reloads once. */
export function applyUpdate(): void {
  const waiting = registration?.waiting;
  if (!waiting) {
    window.location.reload();
    return;
  }
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Sign-out: asks the worker to drop anything that is not a static build file.
 * It never stores user data (src/pwa/sw.js), so this is defence in depth.
 */
export function purgeOfflineCaches(): void {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'PURGE' });
  } catch {
    // No worker: nothing cached.
  }
}

/** Where the worker may run: a real browser tab on https (or localhost), production build. */
export function canUseServiceWorker(): boolean {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false;
  if (!window.isSecureContext) return false;
  // The desktop app is already an installed app with its own updater.
  if (desktopBridge()) return false;
  return true;
}

const UPDATE_CHECK_MS = 30 * 60 * 1000;

export function startPwa(opts: { register: boolean }): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    // Keep the browser's mini-infobar away; Velora shows its own sheet.
    e.preventDefault();
    set({ deferredPrompt: e as BeforeInstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => {
    set({ installedNow: true, deferredPrompt: null });
  });
  if (!opts.register || !canUseServiceWorker()) return;
  const go = () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        registration = reg;
        watchForUpdate(reg);
        // Long-lived tabs (front desk) pick up deploys without a reload.
        setInterval(() => void reg.update().catch(() => undefined), UPDATE_CHECK_MS);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') void reg.update().catch(() => undefined);
        });
      })
      .catch((err: unknown) => {
        console.warn('[pwa] service worker registration failed', err);
      });
  };
  if (document.readyState === 'complete') go();
  else window.addEventListener('load', go, { once: true });
}
